import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicNavbar from '@/components/layout/PublicNavbar';
import PublicFooter from '@/components/layout/PublicFooter';
import { ArrowRight, ShieldCheck, Star, Award, Boxes, Settings, FlaskConical, CheckCircle2 } from 'lucide-react';
import ProductTypesSection from '@/components/shared/ProductTypesSection';

export default function MaklonKosmetikPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] text-[#4A3B32] font-sans selection:bg-[#D4AF37] selection:text-white">
        <PublicNavbar />

        {/* Hero Section */}
        <section className="relative pt-24 pb-32 px-6 lg:px-12 bg-gradient-to-br from-[#FFFDF9] to-[#F9F5EC] overflow-hidden" data-aos="fade-up">
          <div className="absolute right-0 top-0 bottom-0 w-full lg:w-1/2 opacity-20 lg:opacity-100 z-0 hidden md:block">
            <img src="https://images.unsplash.com/photo-1620916566398-39f1143ab7be?q=80&w=1200&auto=format&fit=crop" alt="Cosmetic Products" className="w-full h-full object-cover object-left mask-image-gradient-left" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F9F5EC] via-[#F9F5EC]/80 to-transparent"></div>
          </div>

          <div className="container mx-auto max-w-7xl relative z-10">
            <div className="w-full lg:w-1/2 pt-10">
              <span className="inline-block py-1.5 px-4 bg-[#E6D5B8]/40 text-[#8C6208] text-[10px] font-bold tracking-widest uppercase rounded mb-4">
                MAKLON KOSMETIK PREMIUM
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight mb-6 leading-[1.15] text-[#302621]">
                Maklon Kosmetik <br /> Berkualitas, Aman & <br /> Siap Bersaing
              </h1>
              <p className="text-base text-[#7A695B] mb-10 leading-relaxed max-w-md font-light">
                Kami adalah partner maklon kosmetik terpercaya yang siap membantu Anda mewujudkan brand dengan produk berkualitas tinggi, terpercaya, dan sesuai standar.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 text-base rounded-lg shadow-lg shadow-[#D4AF37]/20 border-0 transition-transform hover:-translate-y-1">
                  Konsultasi Sekarang <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => document.getElementById('layanan-spesialis')?.scrollIntoView({ behavior: 'smooth' })} className="border-[#302621] text-[#302621] bg-transparent hover:bg-[#302621] hover:text-white px-8 py-6 text-base rounded-lg transition-transform hover:-translate-y-1">
                  Lihat Layanan
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Row */}
        <section className="relative -mt-16 px-6 lg:px-12 z-20" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { count: "150+", label: "Brand Kosmetik", icon: <Award /> },
                { count: "500+", label: "Variant Unggulan", icon: <Boxes /> },
                { count: "100rb+", label: "Produk Terjual", icon: <Settings /> },
                { count: "99%", label: "Legalitas Terjamin", icon: <CheckCircle2 /> }
              ].map((stat, idx) => (
                <div key={idx} className="bg-white p-6 md:p-8 rounded-2xl shadow-xl shadow-[#302621]/5 border border-[#E6D5B8]/30 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-[#F9F5EC] flex items-center justify-center text-[#D4AF37] mb-4">
                    {stat.icon}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-[#302621] mb-1">{stat.count}</h3>
                  <p className="text-xs text-[#7A695B] font-medium uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Keunggulan */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Kenapa Memilih Maklon Kosmetik di Satala Dermatech?</h2>
            <p className="text-[#7A695B] mb-16 max-w-2xl mx-auto">Kami memberikan solusi maklon kosmetik yang lengkap dengan kualitas premium.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {[
                { title: "Legalitas Lengkap", desc: "Produk aman dengan dukungan BPOM & Halal.", icon: <ShieldCheck /> },
                { title: "Kualitas Terjamin", desc: "Menggunakan bahan baku berkualitas dan proses modern.", icon: <Star /> },
                { title: "Tim Ahli Berpengalaman", desc: "Didukung oleh tim R&D dan formulator profesional.", icon: <FlaskConical /> },
                { title: "MOQ Fleksibel", desc: "Minimum order yang fleksibel sesuai kebutuhan brand.", icon: <Boxes /> },
                { title: "Layanan End-to-End", desc: "Dari konsep, formulasi, produksi hingga pengiriman.", icon: <Settings /> }
              ].map((feat, idx) => (
                <div key={idx} className="bg-[#FCFAF6] border border-[#E6D5B8]/40 p-6 rounded-2xl flex flex-col items-center text-center hover:border-[#D4AF37] hover:shadow-sm transition-all group">
                  <div className="w-16 h-16 bg-white rounded-full shadow-sm border border-[#E6D5B8]/30 flex items-center justify-center text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform">
                    {feat.icon}
                  </div>
                  <h4 className="font-bold text-[#302621] mb-3 text-sm">{feat.title}</h4>
                  <p className="text-xs text-[#7A695B] leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Alur Maklon Kosmetik */}
        <section className="py-24 px-6 lg:px-12 bg-[#FCFAF6] border-y border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Alur Maklon Kosmetik</h2>
            <p className="text-[#7A695B] mb-16 max-w-2xl mx-auto">Proses mudah dan terstruktur untuk mewujudkan produk impian Anda.</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative max-w-5xl mx-auto">
              {[
                { no: "01", title: "Konsultasi", desc: "Diskusi awal ide dan konsep produk" },
                { no: "02", title: "Pembuatan Sample", desc: "Pengembangan dan pengujian sampel formula" },
                { no: "03", title: "Pendaftaran Legalitas", desc: "Pengurusan izin BPOM, Halal, dan HAKI" },
                { no: "04", title: "Desain Packaging", desc: "Pemilihan dan desain kemasan produk" },
                { no: "05", title: "Po Produksi", desc: "Proses produksi skala massal sesuai PO" },
                { no: "06", title: "Pengujian Qualitas", desc: "Uji kontrol kualitas (QC) hasil produksi" },
                { no: "07", title: "Pelunasan Pembayaran", desc: "Penyelesaian administrasi sebelum kirim" },
                { no: "08", title: "Pengiriman Barang", desc: "Produk siap dikirimkan ke lokasi Anda" }
              ].map((step, idx) => (
                <div key={idx} className="relative z-10 flex flex-col items-center w-full mb-4">
                  <div className="w-12 h-12 rounded-full bg-white border-2 border-[#D4AF37] shadow-sm flex items-center justify-center text-[#8C6208] font-bold text-sm mb-4">
                    {step.no}
                  </div>
                  <h4 className="font-bold text-[#302621] text-sm mb-2 text-center">{step.title}</h4>
                  <p className="text-[11px] text-[#7A695B] text-center leading-relaxed px-2">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Persyaratan Maklon */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h3 className="text-[#8C6208] font-bold tracking-widest text-xs mb-3 uppercase">Persyaratan Maklon</h3>
              <h2 className="text-3xl font-bold text-[#302621]">Melengkapi Badan Usaha / Perseorangan</h2>
            </div>
            
            <div className="flex flex-col md:flex-row gap-8 max-w-4xl mx-auto">
              {/* Dokumen Perseorangan */}
              <div className="w-full md:w-1/2 bg-[#FCFAF6] border border-[#E6D5B8]/40 rounded-3xl p-8 shadow-sm">
                <div className="w-16 h-16 bg-white rounded-full border border-[#E6D5B8]/40 flex items-center justify-center text-[#D4AF37] mb-6 shadow-sm mx-auto">
                  <span className="font-bold text-xl">1</span>
                </div>
                <h4 className="text-xl font-bold text-[#302621] text-center mb-6">Dokumen Perseorangan</h4>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4 bg-white p-4 rounded-xl border border-[#E6D5B8]/20 shadow-sm">
                    <CheckCircle2 className="text-[#D4AF37] w-5 h-5 shrink-0" />
                    <span className="text-[#4A3B32] font-medium text-sm">e-KTP Perseorangan</span>
                  </li>
                  <li className="flex items-center gap-4 bg-white p-4 rounded-xl border border-[#E6D5B8]/20 shadow-sm">
                    <CheckCircle2 className="text-[#D4AF37] w-5 h-5 shrink-0" />
                    <span className="text-[#4A3B32] font-medium text-sm">NPWP Perseorangan</span>
                  </li>
                  <li className="flex items-center gap-4 bg-white p-4 rounded-xl border border-[#E6D5B8]/20 shadow-sm">
                    <CheckCircle2 className="text-[#D4AF37] w-5 h-5 shrink-0" />
                    <span className="text-[#4A3B32] font-medium text-sm">Merek yang sudah HAKI</span>
                  </li>
                  <li className="flex items-center gap-4 bg-white p-4 rounded-xl border border-[#E6D5B8]/20 shadow-sm">
                    <CheckCircle2 className="text-[#D4AF37] w-5 h-5 shrink-0" />
                    <span className="text-[#4A3B32] font-medium text-sm">Surat Kuasa Merek</span>
                  </li>
                </ul>
              </div>

              {/* Dokumen Perusahaan */}
              <div className="w-full md:w-1/2 bg-white border border-[#E6D5B8] rounded-3xl p-8 shadow-xl relative overflow-hidden">
                <div className="absolute -right-10 -top-10 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-2xl"></div>
                <div className="w-16 h-16 bg-[#F9F5EC] rounded-full border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] mb-6 shadow-sm mx-auto relative z-10">
                  <span className="font-bold text-xl">2</span>
                </div>
                <h4 className="text-xl font-bold text-[#302621] text-center mb-6 relative z-10">Dokumen Perusahaan</h4>
                <ul className="space-y-4 relative z-10">
                  <li className="flex items-center gap-4 bg-[#FCFAF6] p-4 rounded-xl border border-[#E6D5B8]/40 shadow-sm">
                    <CheckCircle2 className="text-[#D4AF37] w-5 h-5 shrink-0" />
                    <span className="text-[#4A3B32] font-medium text-sm">e-KTP Direktur</span>
                  </li>
                  <li className="flex items-center gap-4 bg-[#FCFAF6] p-4 rounded-xl border border-[#E6D5B8]/40 shadow-sm">
                    <CheckCircle2 className="text-[#D4AF37] w-5 h-5 shrink-0" />
                    <span className="text-[#4A3B32] font-medium text-sm">NPWP Perusahaan</span>
                  </li>
                  <li className="flex items-center gap-4 bg-[#FCFAF6] p-4 rounded-xl border border-[#E6D5B8]/40 shadow-sm">
                    <CheckCircle2 className="text-[#D4AF37] w-5 h-5 shrink-0" />
                    <span className="text-[#4A3B32] font-medium text-sm">SIUP</span>
                  </li>
                  <li className="flex items-center gap-4 bg-[#FCFAF6] p-4 rounded-xl border border-[#E6D5B8]/40 shadow-sm">
                    <CheckCircle2 className="text-[#D4AF37] w-5 h-5 shrink-0" />
                    <span className="text-[#4A3B32] font-medium text-sm">NIB</span>
                  </li>
                  <li className="flex items-center gap-4 bg-[#FCFAF6] p-4 rounded-xl border border-[#E6D5B8]/40 shadow-sm">
                    <CheckCircle2 className="text-[#D4AF37] w-5 h-5 shrink-0" />
                    <span className="text-[#4A3B32] font-medium text-sm">Merek yang sudah HAKI</span>
                  </li>
                  <li className="flex items-center gap-4 bg-[#FCFAF6] p-4 rounded-xl border border-[#E6D5B8]/40 shadow-sm">
                    <CheckCircle2 className="text-[#D4AF37] w-5 h-5 shrink-0" />
                    <span className="text-[#4A3B32] font-medium text-sm">Surat Kuasa Merek</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Kategori Produk */}
        <ProductTypesSection 
          title="Kategori Produk yang Dapat Dimaklon" 
          subtitle="Berbagai jenis parfum yang dapat kami produksi untuk brand Anda." 
        />

        {/* Layanan Tambahan Khusus */}
        <section id="layanan-spesialis" className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Layanan Spesialis Kami</h2>
            <p className="text-[#7A695B] mb-16 max-w-2xl mx-auto">Kami juga menyediakan layanan spesifik yang dapat disesuaikan dengan kebutuhan pengembangan brand Anda.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { title: "Pengembangan Formulasi", desc: "Riset dan pengembangan formula parfum unik khusus untuk brand Anda.", link: "/layanan/formulasi", img: "/Layanan Kami/konsultasi pengembangan.png" },
                { title: "Legalitas Kosmetika", desc: "Pengurusan sertifikasi BPOM, Halal, hingga pendaftaran HKI.", link: "/layanan/legalitas", img: "/Layanan Kami/Legal kosmetika.png" },
                { title: "Rekomendasi Desain", desc: "Perancangan identitas visual dan kemasan produk yang premium.", link: "/layanan/desain-kemasan", img: "/Layanan Kami/Rekomendasi Design.png" }
              ].map((svc, idx) => (
                <div key={idx} onClick={() => navigate(svc.link)} className="bg-[#FCFAF6] border border-[#E6D5B8]/40 p-4 rounded-2xl flex flex-col text-left hover:border-[#D4AF37] hover:shadow-md transition-all cursor-pointer group">
                  <div className="w-full aspect-[4/3] bg-gray-100 rounded-xl mb-6 overflow-hidden relative">
                    <img src={svc.img} alt={svc.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <h4 className="font-bold text-[#302621] mb-2 text-base group-hover:text-[#D4AF37] transition-colors">{svc.title}</h4>
                  <p className="text-xs text-[#7A695B] leading-relaxed mb-6 flex-grow">{svc.desc}</p>
                  <div className="flex items-center gap-2 text-[#D4AF37] font-bold text-xs tracking-wider">
                    SELENGKAPNYA <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 lg:px-12 bg-[#F9F5EC] border-t border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Siap Memulai Brand Kosmetik Anda?</h2>
            <p className="text-[#7A695B] mb-8">Bersama Satala Dermatech, wujudkan brand kosmetik yang berkualitas dan dipercaya banyak konsumen.</p>
            <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 rounded-lg shadow-md border-0 transition-transform hover:-translate-y-1">
              Konsultasi Sekarang <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <PublicFooter />
      </div>
    </>
  );
}
